import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap';
import { changesApi } from '../../../services/changesApi';
import type {
  ChangeApproval,
  ChangeExtraFields,
  ChangeExtraValue,
  ChangeFormField,
  ChangeRequest,
} from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import {
  APPROVAL_TRACKS,
  buildTransportForceRespawnConfirmDetails,
  formatTransportDispatchStepLines,
  buildVoidAllApprovalsConfirmDetails,
  buildVoidSlotConfirmDetails,
  canManageTransportApprovalOps,
  canRespawnSingleTransportSlot,
  canVoidApprovals,
  committeeNeedsNewApprovalRound,
  hasDecidedCommitteeApproval,
  listTransportApprovalTargetLabels,
  partitionCommitteeApprovalSlotsForDisplay,
  partitionTransportApprovalSlotsForDisplay,
  sortCommitteeApprovalSlotsForDisplay,
  sortTransportApprovalSlotsForDisplay,
  transportSlotInvalidatedByRejection,
  transportSlotSupersededByRespawn,
} from '../changeApprovalPlan';
import {
  buildExtraFieldsPayloadFromNatives,
  extraFieldVisibleFromNatives,
  hydrateExtraFields,
  validateExtraFieldsFromNatives,
} from '../changeFormSchema';
import { FORM_EDITABLE_STATUSES, isEmergencyChangeType } from '../changeLabels';
import ChangeExtraFieldInput from './ChangeExtraFieldInput';

interface Props {
  change: ChangeRequest;
  canVoidApprovals?: boolean;
  /** Comitê (Autorizar) vs GTEC/GSERV-I em Transporte. */
  approvalScope?: 'all' | 'committee' | 'transport';
  /** Barra da aba Transporte renderiza o botão no pai. */
  suppressVoidAllButton?: boolean;
  onUpdated: () => Promise<void> | void;
}

function approvalStatusLabel(status?: string | null) {
  if (status === 'aprovado') return 'Aprovado';
  if (status === 'rejeitado' || status === 'reprovado') return 'Reprovado';
  if (status === 'cancelado') return 'Cancelada';
  if (status === 'pendente') return 'Pendente';
  return status || '—';
}

function approvalStatusBadge(status?: string | null) {
  const label = approvalStatusLabel(status);
  if (status === 'aprovado') return <Badge bg="success">{label}</Badge>;
  if (status === 'rejeitado' || status === 'reprovado') return <Badge bg="danger">{label}</Badge>;
  if (status === 'pendente') return <Badge bg="warning" text="dark">{label}</Badge>;
  if (status === 'cancelado') return <Badge bg="secondary">{label}</Badge>;
  return <Badge bg="secondary">{label}</Badge>;
}

function approvalCardClassName(status?: string | null) {
  if (status === 'aprovado') return 'mb-3 border-success';
  if (status === 'rejeitado' || status === 'reprovado') return 'mb-3 border-danger';
  return 'mb-3';
}

function approvalSlotAreaLabel(slot: ChangeApproval): string {
  if (slot.track === 'gestao_mudancas' && !slot.group_id) {
    return 'Gestor de mudanças';
  }
  if (slot.assignment_kind === 'pessoa') {
    return `${slot.assignee_name || `Usuário #${slot.assignee_id}`} · ${
      slot.group_name || 'área'
    }`;
  }
  return slot.group_name || (slot.group_id ? `área #${slot.group_id}` : 'área');
}

const ChangeApprovalsTab: React.FC<Props> = ({
  change,
  canVoidApprovals: canVoidProp,
  approvalScope = 'all',
  suppressVoidAllButton = false,
  onUpdated,
}) => {
  const [justifications, setJustifications] = useState<Record<number, string>>({});
  const [extrasBySlot, setExtrasBySlot] = useState<Record<number, ChangeExtraFields>>({});
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [voiding, setVoiding] = useState<number | 'all' | null>(null);
  const [respawning, setRespawning] = useState<number | 'all' | 'force' | null>(null);
  const { user } = useAuth();
  const { showConfirm } = useModal();
  const inAuthorize = change.status === 'autorizar';
  const newRoundInAgendado =
    change.status === 'agendado' && hasDecidedCommitteeApproval(change.approvals);
  const inDecisionPhase =
    approvalScope === 'transport'
      ? change.status === 'transporte'
      : inAuthorize || newRoundInAgendado;
  const transportReadOnly =
    approvalScope === 'transport' && change.status !== 'transporte';
  const canVoid = canVoidProp ?? canVoidApprovals(change, user, change.approval_settings);
  const canManageTransportRespawn =
    approvalScope === 'transport' &&
    change.status === 'transporte' &&
    canManageTransportApprovalOps(change, user);
  const transportTargetLabels = useMemo(
    () => listTransportApprovalTargetLabels(change),
    [change],
  );
  const transportDispatchStepLines = useMemo(
    () => formatTransportDispatchStepLines(change),
    [change],
  );
  const showForceTransportRespawn =
    canManageTransportRespawn && transportTargetLabels.length > 0;
  /** Redisparo parcial (todas reprovadas/aprovadas) foi removido da UI — só nova rodada ou por área no histórico. */
  const formFields = (change.approval_settings?.form_fields || []) as ChangeFormField[];
  const formFieldsKey = formFields.map((item) => item.id).join(',');

  const gestaoManagerSlots = useMemo(
    () =>
      sortCommitteeApprovalSlotsForDisplay(
        (change.approvals || []).filter(
          (slot) => slot.track === 'gestao_mudancas' && !slot.group_id,
        ),
      ),
    [change.approvals],
  );
  const gestaoManagerActiveSlots = gestaoManagerSlots.filter((slot) => slot.status !== 'cancelado');
  const gestaoManagerCanceledSlots = gestaoManagerSlots.filter((slot) => slot.status === 'cancelado');
  const slots = useMemo(() => {
    const withGroup = (change.approvals || []).filter((slot) => slot.group_id);
    if (approvalScope === 'transport') {
      return sortTransportApprovalSlotsForDisplay(
        withGroup.filter((slot) => slot.track === 'transporte'),
      );
    }
    return withGroup;
  }, [approvalScope, change.approvals]);
  const groupedSlots = useMemo(() => {
    const groups = new Map<string, ChangeApproval[]>();
    for (const slot of slots) {
      const key = slot.track || '';
      const list = groups.get(key) || [];
      list.push(slot);
      groups.set(key, list);
    }
    const emergencyUi =
      isEmergencyChangeType(change.change_type) && approvalScope === 'transport';
    const grouped: Array<{ track: string; label: string; items: ChangeApproval[] }> = [
      {
        track: 'emergencial',
        label: 'Emergencial',
        items: groups.get('emergencial') || [],
      },
      ...APPROVAL_TRACKS.map((item) => ({
        track: item.track,
        label: item.label,
        items: sortCommitteeApprovalSlotsForDisplay(groups.get(item.track) || []),
      })),
      {
        track: 'transporte',
        label: emergencyUi ? 'Aprovação emergencial' : 'Transporte',
        items: groups.get('transporte') || [],
      },
    ];
    if (groups.has('')) {
      grouped.push({ track: '', label: 'Aprovação', items: groups.get('') || [] });
    }
    const withItems = grouped.filter((group) => group.items.length);
    if (approvalScope === 'committee') {
      return withItems.filter((group) => group.track !== 'transporte');
    }
    if (approvalScope === 'transport') {
      return withItems.filter((group) => group.track === 'transporte');
    }
    return withItems;
  }, [change.change_type, slots, approvalScope]);

  useEffect(() => {
    const next: Record<number, ChangeExtraFields> = {};
    for (const slot of change.approvals || []) {
      next[slot.id] = hydrateExtraFields(formFields, slot.extra_fields);
    }
    setExtrasBySlot(next);
  }, [change.id, change.status, formFieldsKey, (change.approvals || []).map((s) => `${s.id}:${s.status}`).join('|')]);

  const setSlotExtra = (slotId: number, fieldId: number, value: ChangeExtraValue) => {
    setExtrasBySlot((prev) => ({
      ...prev,
      [slotId]: { ...(prev[slotId] || {}), [String(fieldId)]: value },
    }));
  };

  const visibleFieldsFor = (extras: ChangeExtraFields) =>
    formFields.filter((field) => extraFieldVisibleFromNatives(field, formFields, {}, extras));

  const handleDecision = async (slot: ChangeApproval, approved: boolean) => {
    const justification = (justifications[slot.id] || '').trim();
    if (!justification) {
      setError('A justificativa é obrigatória.');
      return;
    }
    const extras = extrasBySlot[slot.id] || {};
    const extraError = validateExtraFieldsFromNatives(formFields, {}, extras);
    if (extraError) {
      setError(extraError);
      return;
    }
    const extraPayload = buildExtraFieldsPayloadFromNatives(formFields, {}, extras);
    const areaLabel = approvalSlotAreaLabel(slot);
    const ok = await showConfirm(
      approved
        ? {
            title: 'Confirmar aprovação?',
            message: `Registrar aprovação de ${areaLabel}?`,
            details: `Justificativa: ${justification}`,
            confirmLabel: 'Sim, aprovar',
            cancelLabel: 'Cancelar',
            variant: 'primary',
          }
        : {
            title: 'Confirmar reprovação?',
            message: `Registrar reprovação de ${areaLabel}?`,
            details:
              'A mudança pode voltar para Agendado para ajustes. Justificativa: ' + justification,
            confirmLabel: 'Sim, reprovar',
            cancelLabel: 'Cancelar',
            variant: 'danger',
          },
    );
    if (!ok) return;

    setError(null);
    setSavingId(slot.id);
    try {
      if (approved) {
        await changesApi.approve(change.id, slot.id, justification, extraPayload);
      } else {
        await changesApi.reject(change.id, slot.id, justification, extraPayload);
      }
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao registrar a decisão.'));
    } finally {
      setSavingId(null);
    }
  };

  const handleVoid = async () => {
    const ok = await showConfirm({
      title: 'Cancelar todas as aprovações de área?',
      message:
        'Confira abaixo o que será anulado. Você pode voltar sem alterar nada ou confirmar para replanejar o comitê.',
      details: buildVoidAllApprovalsConfirmDetails(change.approvals),
      variant: 'danger',
      confirmLabel: 'Sim, cancelar aprovações',
      cancelLabel: 'Não cancelar',
    });
    if (!ok) return;
    setVoiding('all');
    setError(null);
    try {
      await changesApi.voidApprovals(change.id);
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao cancelar as aprovações.'));
    } finally {
      setVoiding(null);
    }
  };

  const handleForceRespawnTransport = async () => {
    const ok = await showConfirm({
      title: 'Nova rodada de Transporte?',
      message:
        'Confira quem receberá aprovação. Pendências atuais serão canceladas e novas serão disparadas.',
      details: buildTransportForceRespawnConfirmDetails(transportTargetLabels),
      confirmLabel: 'Sim, cancelar pendentes e redisparar',
      cancelLabel: 'Não',
      variant: 'warning',
    });
    if (!ok) return;
    setRespawning('force');
    setError(null);
    try {
      await changesApi.respawnTransportApprovals(change.id, {
        all_areas: true,
        force_new_round: true,
      });
      await onUpdated();
    } catch (err: unknown) {
      setError(
        getApiErrorMessage(err, 'Não foi possível redisparar as aprovações de Transporte.'),
      );
    } finally {
      setRespawning(null);
    }
  };

  const handleRespawnTransportSlot = async (slot: ChangeApproval) => {
    const area = slot.group_name || `área #${slot.group_id}`;
    const ok = await showConfirm({
      title: `Nova aprovação — ${area}?`,
      message:
        'Será criado um slot pendente novo para esta área. A decisão reprovada anterior continua visível no histórico.',
      confirmLabel: 'Sim, abrir pendência',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    setRespawning(slot.id);
    setError(null);
    try {
      await changesApi.respawnTransportApprovals(change.id, { approval_id: slot.id });
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível abrir nova aprovação desta área.'));
    } finally {
      setRespawning(null);
    }
  };

  const handleVoidSlot = async (slot: ChangeApproval) => {
    const ok = await showConfirm({
      title: 'Cancelar esta aprovação de área?',
      message:
        'A decisão fica cancelada no histórico e a área permanece no comitê. A mudança não vai para Transporte. Para disparar de novo, salve o plano com as áreas escolhidas.',
      details: buildVoidSlotConfirmDetails(slot),
      variant: 'danger',
      confirmLabel: 'Sim, cancelar esta aprovação',
      cancelLabel: 'Não cancelar',
    });
    if (!ok) return;
    setVoiding(slot.id);
    setError(null);
    try {
      await changesApi.voidApproval(change.id, slot.id);
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao cancelar a aprovação.'));
    } finally {
      setVoiding(null);
    }
  };

  const voidAllButton = canVoid ? (
    <div className="mb-3">
      <Button
        size="sm"
        variant="outline-danger"
        onClick={() => void handleVoid()}
        disabled={voiding !== null}
      >
        {voiding === 'all' ? 'Cancelando...' : 'Cancelar todas as aprovações'}
      </Button>
    </div>
  ) : null;

  const renderDecisionActions = (slot: ChangeApproval) => {
    const pending = slot.status === 'pendente';
    const canDecideSlot = Boolean(
      pending && inDecisionPhase && slot.can_decide && !transportReadOnly,
    );
    return (
      <div className="d-flex flex-wrap align-items-center gap-2">
        {canDecideSlot ? (
          <>
            <Button
              size="sm"
              disabled={savingId === slot.id || voiding !== null}
              onClick={() => void handleDecision(slot, true)}
            >
              Aprovar
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              disabled={savingId === slot.id || voiding !== null}
              onClick={() => void handleDecision(slot, false)}
            >
              Reprovar
            </Button>
          </>
        ) : pending ? (
          <span className="text-muted small">
            {slot.wait_reason ||
              `Aguardando aprovação de ${slot.assignee_name || slot.group_name || 'área'}`}
          </span>
        ) : null}
        {canVoid && slot.group_id && slot.status !== 'cancelado' ? (
          <Button
            size="sm"
            variant="outline-danger"
            className="flex-shrink-0"
            disabled={voiding !== null || respawning !== null}
            aria-label="Cancelar esta aprovação"
            onClick={() => void handleVoidSlot(slot)}
          >
            {voiding === slot.id ? 'Cancelando...' : 'Cancelar'}
          </Button>
        ) : null}
        {canManageTransportRespawn &&
        canRespawnSingleTransportSlot(change.approvals, slot) ? (
          <Button
            size="sm"
            variant="outline-primary"
            className="flex-shrink-0"
            disabled={respawning !== null || voiding !== null}
            onClick={() => void handleRespawnTransportSlot(slot)}
          >
            {respawning === slot.id ? 'Abrindo...' : 'Redisparar só esta área (reprovada)'}
          </Button>
        ) : null}
      </div>
    );
  };

  const renderApprovalAreaCard = (slot: ChangeApproval) => {
    const pending = slot.status === 'pendente';
    const canDecideSlot = Boolean(
      pending && inDecisionPhase && slot.can_decide && !transportReadOnly,
    );
    const areaLabel = approvalSlotAreaLabel(slot);
    return (
      <Card
        key={slot.id}
        className={approvalCardClassName(slot.status)}
        data-testid={`approval-area-card-${slot.id}`}
      >
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
            <div>
              <strong>{areaLabel}</strong>
              {slot.is_sap_request_transport ? (
                <div className="small text-primary fw-semibold mt-1">
                  Aprovação extra — SAP com request
                </div>
              ) : null}
              {slot.approver_name || slot.assignee_name ? (
                <div className="small text-muted">
                  Por {slot.approver_name || slot.assignee_name}
                </div>
              ) : null}
            </div>
            {approvalStatusBadge(slot.status)}
          </div>
          {canDecideSlot ? (
            renderDecisionFields(slot)
          ) : (
            <>
              {!pending ? (
                <div className="mb-2">
                  {transportSlotInvalidatedByRejection(slot.justification) ? (
                    <Alert variant="warning" className="py-1 px-2 small mb-2">
                      Decisão invalidada por reprovação em Transporte (histórico; não vale na rodada
                      atual).
                    </Alert>
                  ) : null}
                  {transportSlotSupersededByRespawn(slot.justification) ? (
                    <Alert variant="secondary" className="py-1 px-2 small mb-2">
                      Decisão substituída ao abrir nova rodada de Transporte (não vale para a rodada
                      atual).
                    </Alert>
                  ) : null}
                  <div className="small text-muted mb-1">Justificativa</div>
                  <div className="small">{slot.justification || '—'}</div>
                  {slot.decided_at ? (
                    <div className="small text-muted mt-1">
                      Decidido em {new Date(slot.decided_at).toLocaleString('pt-BR')}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="mb-2 small">{slot.justification || '—'}</div>
              )}
              {pending || canVoid || canManageTransportRespawn
                ? renderDecisionActions(slot)
                : null}
            </>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderDecisionFields = (slot: ChangeApproval) => {
    const extras = extrasBySlot[slot.id] || {};
    const visibleFields = visibleFieldsFor(extras);
    return (
      <>
        {visibleFields.map((field) => (
          <Form.Group className="mb-3" key={`${slot.id}-${field.id}`}>
            <Form.Label>{field.label}</Form.Label>
            <ChangeExtraFieldInput
              field={field}
              value={extras[String(field.id)]}
              onChange={(value: ChangeExtraValue) => setSlotExtra(slot.id, field.id, value)}
            />
            {field.help_text ? <Form.Text className="text-muted">{field.help_text}</Form.Text> : null}
          </Form.Group>
        ))}
        <Form.Label htmlFor={`justification-${slot.id}`}>Justificativa</Form.Label>
        <Form.Control
          id={`justification-${slot.id}`}
          as="textarea"
          rows={2}
          className="mb-3"
          value={justifications[slot.id] || ''}
          onChange={(e) =>
            setJustifications((prev) => ({ ...prev, [slot.id]: e.target.value }))
          }
        />
        {renderDecisionActions(slot)}
      </>
    );
  };

  const renderGestaoManagerCard = (slot: ChangeApproval) => {
    const pending = slot.status === 'pendente';
    const canceled = slot.status === 'cancelado';
    const canDecideSlot = Boolean(
      pending && inDecisionPhase && slot.can_decide && !transportReadOnly,
    );
    return (
      <Card
        key={slot.id}
        className={approvalCardClassName(slot.status).replace('mb-3', 'mb-4')}
        data-testid="gestao-manager-approval-card"
      >
        <Card.Header className="fw-semibold d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span>Gestor de mudanças</span>
          {approvalStatusBadge(slot.status)}
        </Card.Header>
        <Card.Body>
          {pending ? (
            <p className="text-muted small mb-3">
              Quem tem a permissão de Gestor de mudanças (ou Administrador) aprova ou reprova aqui,
              mesmo sem pertencer a um grupo. Justificativa obrigatória.
            </p>
          ) : null}
          {slot.approver_name ? (
            <div className="mb-2 small text-muted">Por {slot.approver_name}</div>
          ) : null}
          {canDecideSlot ? (
            renderDecisionFields(slot)
          ) : (
            <>
              {!pending ? (
                <div className="mb-2">
                  <div className="small text-muted mb-1">Justificativa</div>
                  <div>{slot.justification || '—'}</div>
                </div>
              ) : (
                <div className="mb-2">{slot.justification || '—'}</div>
              )}
              {pending && !canceled ? renderDecisionActions(slot) : null}
            </>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderGestaoManagerSection = () => {
    if (!gestaoManagerActiveSlots.length) return null;
    return <>{gestaoManagerActiveSlots.map((slot) => renderGestaoManagerCard(slot))}</>;
  };

  if (
    isEmergencyChangeType(change.change_type) &&
    approvalScope === 'committee'
  ) {
    return (
      <Alert variant="light" className="mb-0">
        Mudanças <strong>emergenciais</strong> não usam comitê (CAMUD/Riscos). O gestor imediato,
        GTEC e GSERV-I aprovam na aba <strong>Transporte</strong>
        {change.status === 'transporte' ||
        change.status === 'implementar' ||
        change.status === 'revisar'
          ? '.'
          : ' após avançar para Transporte (salto automático ao ir para Autorizar).'}
      </Alert>
    );
  }

  if (approvalScope === 'transport' && change.status !== 'transporte' && !groupedSlots.length) {
    return (
      <Alert variant="light" className="mb-0">
        {change.status === 'implementar'
          ? 'As aprovações de Transporte (GTEC e GSERV-I) já foram registradas. Use o bloco acima se ainda faltar o chamado de Infra.'
          : isEmergencyChangeType(change.change_type)
            ? 'Avance o workflow para Autorizar (emergencial: salto automático para Transporte) para decidir as aprovações desta aba.'
            : 'As aprovações de Transporte aparecem aqui quando a mudança estiver no status Transporte.'}
      </Alert>
    );
  }

  const committeeHistoryVisible = hasDecidedCommitteeApproval(change.approvals);
  const showPreAuthorizeHint =
    approvalScope !== 'transport' &&
    FORM_EDITABLE_STATUSES.has(change.status) &&
    !committeeHistoryVisible;

  if (showPreAuthorizeHint) {
    if (isEmergencyChangeType(change.change_type)) {
      return (
        <Alert variant="light" className="mb-0">
          Nesta mudança emergencial a ordem é <strong>solicitante → grupo da área → gestor</strong>,
          depois GTEC, GSERV-I e demais áreas fixas. Ao ir para <strong>Autorizar</strong> (salto
          automático para <strong>Transporte</strong>), use a aba <strong>Transporte</strong> para
          aprovar — esta aba não traz comitê.
        </Alert>
      );
    }
    return (
      <Alert variant="light" className="mb-0">
        As aprovações são pedidas no status <strong>Autorizar</strong>. Lá o criador ou um
        administrador escolhe os grupos no bloco <strong>Quem aprova esta mudança</strong> e as
        áreas aprovam ou reprovam nesta aba.
      </Alert>
    );
  }

  if (
    !slots.length &&
    !(approvalScope === 'transport'
      ? false
      : gestaoManagerActiveSlots.length || gestaoManagerCanceledSlots.length)
  ) {
    return (
      <Alert variant="light" className="mb-0">
        {approvalScope === 'transport'
          ? 'Nenhum card de Transporte nesta mudança. Confira em Cadastros → Aprovações → Aprovação emergencial (ou regra do tipo «Mudança Normal») se GTEC e GSERV-I estão ativos; em Cadastros → Grupos, marque Gestor em quem pode aprovar. Recarregue a página ou use «Nova rodada» / redisparo de Transporte se você é criador ou gestor de mudanças.'
          : change.status === 'transporte'
          ? 'As aprovações de Transporte (GTEC e GSERV-I) estão na aba Transporte. Se a lista estiver vazia, cadastre as áreas fixas.'
          : change.status === 'autorizar'
            ? 'Salve o plano no bloco Quem aprova esta mudança para gerar as aprovações. As áreas escolhidas aprovam ou reprovam nesta mesma aba.'
            : 'As decisões desta mudança ficam nesta aba a partir de Autorizar. O plano é definido lá, no bloco acima.'}
      </Alert>
    );
  }

  const waitingArea = change.emergency_approval?.waiting_area_name;

  return (
    <div>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {!suppressVoidAllButton ? voidAllButton : null}
      {transportReadOnly ? (
        <Alert variant="warning" className="py-2 small mb-3">
          A mudança está em <strong>{change.status}</strong>, não em Transporte. Histórico somente
          leitura — aprovar, reprovar, cancelar ou redisparar só com status{' '}
          <strong>Transporte</strong>.
        </Alert>
      ) : null}
      {approvalScope === 'transport' && !transportReadOnly ? (
        <Alert variant="info" className="py-2 small mb-3">
          Decisões de Transporte só neste status. Emergencial: em Agendado use{' '}
          <strong>Autorizar</strong> no workflow (vai direto para Transporte).{' '}
          <strong>Nova rodada</strong> cancela pendências e redispara conforme o cadastro atual.
        </Alert>
      ) : null}
      {showForceTransportRespawn ? (
        <Card className="mb-3 border-primary" data-testid="transport-respawn-targets">
          <Card.Body className="py-3">
            <div className="fw-semibold mb-1">Quem aprova Transporte nesta mudança</div>
            <p className="text-muted small mb-2">
              Prévia da ordem no momento do redisparo (lê cadastro atual). Quem pode aprovar cada
              card aparece no bloco &quot;Aguardando decisão&quot; abaixo.
            </p>
            <ol className="small mb-3">
              {transportDispatchStepLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ol>
            <div className="d-flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="primary"
                disabled={respawning !== null || voiding !== null}
                onClick={() => void handleForceRespawnTransport()}
              >
                {respawning === 'force'
                  ? 'Redisparando...'
                  : 'Nova rodada (cancelar pendentes e redisparar)'}
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : null}
      {change.status === 'agendado' && committeeHistoryVisible ? (
        <Alert variant="info" className="py-2">
          Histórico das decisões anteriores permanece abaixo. Slots <strong>pendentes</strong> desta
          nova rodada podem ser aprovados ou reprovados aqui; também é possível avançar para{' '}
          <strong>Autorizar</strong> se preferir o fluxo clássico.
        </Alert>
      ) : null}
      {approvalScope !== 'transport' && committeeNeedsNewApprovalRound(change.approvals) ? (
        <Alert variant="warning" className="py-2">
          Esta rodada do comitê foi reprovada ou cancelada. Quem monta o plano (criador, dono ou
          gestor de mudanças) precisa salvar <strong>Quem aprova esta mudança</strong> para criar
          novas pendências. Aprovador de área só decide o card da própria área; não remonta o
          comitê. Quando todos da nova rodada aprovarem, a mudança segue para Transporte.
        </Alert>
      ) : null}
      {inDecisionPhase && waitingArea ? (
        <Alert variant="warning" className="py-2">
          Aguardando primeiro a aprovação de <strong>{waitingArea}</strong>. Os demais gestores só
          decidem depois desta etapa.
        </Alert>
      ) : null}
      {formFields.length > 0 && inDecisionPhase ? (
        <Alert variant="light" className="border py-2 small">
          Campos extras configurados em <strong>Cadastros → Formulário do aprovador</strong> aparecem
          para quem for decidir (além da justificativa).
        </Alert>
      ) : null}
      {approvalScope !== 'transport' ? renderGestaoManagerSection() : null}
      {approvalScope === 'transport' &&
      change.requester_immediate_manager_setup?.active &&
      change.requester_immediate_manager_setup?.needs_assignment ? (
        <Alert variant="warning" className="mb-3" data-testid="requester-immediate-manager-blocked">
          O <strong>1º passo de Transporte (gerente imediato / grupo do solicitante)</strong> não
          tem aprovador: cadastre gestores no grupo ou o gerente imediato individual do solicitante
          em <strong>Cadastros → Grupos → Pessoas</strong>. Depois de ajustar o cadastro, use{' '}
          <strong>Nova rodada (cancelar pendentes e redisparar)</strong> para recriar as pendências
          com o fluxo atual.
        </Alert>
      ) : null}
      {groupedSlots.map((group) => {
        const isTransportList =
          approvalScope === 'transport' && group.track === 'transporte' && group.items.length;
        const transportParts = isTransportList
          ? partitionTransportApprovalSlotsForDisplay(group.items)
          : null;
        const committeeParts =
          !isTransportList && approvalScope !== 'transport'
            ? partitionCommitteeApprovalSlotsForDisplay(group.items, change.approvals)
            : null;
        return (
          <div key={group.track || 'legacy'} className="mb-4">
            <h6 className="mb-2">{group.label}</h6>
            {transportParts ? (
              <>
                {transportParts.pending.length ? (
                  <div className="mb-3" data-testid="transport-pending-block">
                    <div className="small fw-semibold text-primary mb-2">
                      Aguardando decisão ({transportParts.pending.length})
                    </div>
                    <div className="d-flex flex-column gap-0">
                      {transportParts.pending.map((slot) => renderApprovalAreaCard(slot))}
                    </div>
                  </div>
                ) : null}
                {transportParts.history.length ? (
                  <div data-testid="transport-history-block">
                    <div className="small fw-semibold text-muted mb-2">
                      Histórico ({transportParts.history.length})
                    </div>
                    {transportParts.pending.length ? <hr className="my-2" /> : null}
                    {transportParts.history.map((slot) => renderApprovalAreaCard(slot))}
                  </div>
                ) : null}
              </>
            ) : committeeParts ? (
              <>
                {committeeParts.current.length ? (
                  <div className="mb-3" data-testid="committee-current-block">
                    <div className="small fw-semibold text-primary mb-2">
                      Rodada atual ({committeeParts.current.length})
                    </div>
                    <div className="d-flex flex-column gap-0">
                      {committeeParts.current.map((slot) => renderApprovalAreaCard(slot))}
                    </div>
                  </div>
                ) : null}
                {committeeParts.history.length ? (
                  <div data-testid="committee-history-block">
                    <div className="small fw-semibold text-muted mb-2">
                      Histórico ({committeeParts.history.length})
                    </div>
                    {committeeParts.current.length ? <hr className="my-2" /> : null}
                    {committeeParts.history.map((slot) => renderApprovalAreaCard(slot))}
                  </div>
                ) : null}
              </>
            ) : (
              group.items.map((slot) => renderApprovalAreaCard(slot))
            )}
          </div>
        );
      })}
      {approvalScope !== 'transport' && gestaoManagerCanceledSlots.length ? (
        <div className="mb-4" data-testid="gestao-manager-canceled-history">
          <h6 className="mb-2 text-muted">Gestor de mudanças (canceladas)</h6>
          {gestaoManagerCanceledSlots.map((slot) => renderGestaoManagerCard(slot))}
        </div>
      ) : null}
    </div>
  );
};

export default ChangeApprovalsTab;
